/**
 * Non-blocking confirmation dialog.
 * Replaces confirm() with an accessible, async modal dialog.
 */

/**
 * Show a confirmation dialog and return the user's choice.
 *
 * @param message - The question or message to display
 * @param confirmLabel - Text for the confirm button (default: "OK")
 * @param cancelLabel - Text for the cancel button (default: "Cancel")
 * @returns Promise that resolves to true if confirmed, false if cancelled
 */
export function showConfirmDialog(message: string, confirmLabel = 'OK', cancelLabel = 'Cancel'): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
        // Create overlay
        const overlay = document.createElement('div');
        overlay.className = 'confirm-overlay';

        // Create dialog
        const dialog = document.createElement('div');
        dialog.className = 'confirm-dialog';
        dialog.setAttribute('role', 'alertdialog');
        dialog.setAttribute('aria-modal', 'true');
        dialog.setAttribute('aria-label', 'Confirmation');

        // Message
        const msgEl = document.createElement('p');
        msgEl.className = 'confirm-message';
        msgEl.textContent = message;
        msgEl.id = 'confirmDialogMessage';
        dialog.setAttribute('aria-describedby', msgEl.id);

        // Button container
        const btnContainer = document.createElement('div');
        btnContainer.className = 'confirm-buttons';

        // Cancel button
        const cancelBtn = document.createElement('button');
        cancelBtn.className = 'confirm-btn confirm-btn-cancel';
        cancelBtn.textContent = cancelLabel;

        // Confirm button
        const confirmBtn = document.createElement('button');
        confirmBtn.className = 'confirm-btn confirm-btn-confirm';
        confirmBtn.textContent = confirmLabel;

        btnContainer.appendChild(cancelBtn);
        btnContainer.appendChild(confirmBtn);

        dialog.appendChild(msgEl);
        dialog.appendChild(btnContainer);
        overlay.appendChild(dialog);
        document.body.appendChild(overlay);

        // Focus the confirm button for keyboard accessibility
        confirmBtn.focus();

        // Close on Escape key
        const handleKeydown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                cleanup(false);
            }
        };

        function cleanup(result: boolean): void {
            document.removeEventListener('keydown', handleKeydown);
            overlay.remove();
            resolve(result);
        }

        confirmBtn.addEventListener('click', () => cleanup(true));
        cancelBtn.addEventListener('click', () => cleanup(false));

        document.addEventListener('keydown', handleKeydown);

        // Close on overlay click (outside dialog)
        overlay.addEventListener('click', (e) => {
            if (e.target === overlay) {
                cleanup(false);
            }
        });
    });
}
